import { ElementRef, EventEmitter, Injectable, NgZone } from '@angular/core';
import { combineLatest, takeUntil } from 'rxjs';
import { selectClientLocation, selectStoreList, selectStoreLocation } from '@store/selectors';
import { Store } from '@ngrx/store';
import { IAbstractPresenter } from '@core/interfaces';
import { ILocationMapComponent, ILocationMapPresenter } from './interfaces';
import { ClientLocation, Store as StoreModel } from '@core/models';
import { DeliveryZoneMapa } from '@core/models';
import { LIGHT_MAP_STYLES, LIGHT_CIRCLE_OPTIONS } from './themes/light-map-theme';
import { DARK_MAP_STYLES, DARK_CIRCLE_OPTIONS } from './themes/dark-map-theme';
import { IconService, ThemeService } from '@core/services';

@Injectable()
export class LocationMapPresenter extends IAbstractPresenter<ILocationMapComponent> implements ILocationMapPresenter {
	map!: google.maps.Map;
	markers: google.maps.Marker[] = [];
	circles: google.maps.Circle[] = [];
	userMarker: google.maps.Marker | null = null;
	storeBadgeIcon: google.maps.Icon | null = null;
	userBadgeIcon: google.maps.Icon | null = null;
	storeMarkerColor?: string;
	userMarkerColor?: string;
	darkMode = false;
	darkModeMediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

	mapContainer!: ElementRef;
	addressSelected: EventEmitter<string> = new EventEmitter<string>();

	private cachedStores: StoreModel[] = [];
	private cachedClientLocation: ClientLocation | null = null;
	private isMapInitialized = false;

	getStoreColor(): string {
		if (this.view?.storeMarkerColor) return this.view.storeMarkerColor;
		if (this.storeMarkerColor) return this.storeMarkerColor;
		if (typeof window !== 'undefined') {
			const cssVar = getComputedStyle(document.documentElement).getPropertyValue('--background-primary-500').trim()
				|| getComputedStyle(document.documentElement).getPropertyValue('--color-primary-500').trim();
			if (cssVar) return cssVar;
		}
		return '#db9800';
	}

	getUserLocationColor(): string {
		if (this.view?.userMarkerColor) return this.view.userMarkerColor;
		if (this.userMarkerColor) return this.userMarkerColor;
		if (typeof window !== 'undefined') {
			const cssVar = getComputedStyle(document.documentElement).getPropertyValue('--color-neutral-900').trim()
				|| getComputedStyle(document.documentElement).getPropertyValue('--color-neutral-800').trim();
			if (cssVar) return cssVar;
		}
		return '#2b2b2b';
	}

	constructor(
		private store: Store,
		private ngZone: NgZone,
		private iconService: IconService,
		private themeService: ThemeService
	) {
		super();
	}

	dispatchActions(): void {}

	suscribeObservers(): void {
		// Por defecto siempre es claro
		this.darkMode = false;
		if (this.view) {
			this.view.darkMode = false;
		}

		// Escuchar cambios de tema desde ThemeService
		this.themeService.currentTheme$
			.pipe(takeUntil(this.destroy$))
			.subscribe(theme => {
				const isDark = (theme as unknown as string) === 'dark' || (theme as unknown as string).includes('dark');
				if (this.darkMode !== isDark) {
					this.setDarkMode(isDark);
				}
			});

		// combineLatest re-dispara cuando CUALQUIERA de las tres fuentes cambia
		combineLatest([
			this.store.select(selectStoreList),
			this.store.select(selectStoreLocation),
			this.store.select(selectClientLocation),
		])
			.pipe(takeUntil(this.destroy$))
			.subscribe(([storeList, storeLocation, clientLocation]) => {
				let storesToShow: StoreModel[] = [];

				if (storeLocation) {
					storesToShow = [
						{
							...storeLocation,
							location: { lat: storeLocation.lat, lng: storeLocation.lng },
							nombre: storeLocation.address,
						} as unknown as StoreModel,
					];
				} else if (storeList && storeList.length > 0) {
					storesToShow = storeList;
				}

				this.cachedStores = storesToShow;
				this.cachedClientLocation = clientLocation;

				this.tryInitOrUpdateMap();
			});

		this.store
			.select(selectClientLocation)
			.pipe(takeUntil(this.destroy$))
			.subscribe(clientLocation => {
				this.cachedClientLocation = clientLocation;
				if (this.map) {
					this.updateUserMarker(clientLocation);
				}
			});
	}

	override onAfterViewInit(): void {
		this.tryInitOrUpdateMap();
	}

	private darkModeHandler = (e: MediaQueryListEvent): void => {
		this.ngZone.run(() => {
			this.darkMode = e.matches;
			if (this.view) {
				this.view.darkMode = this.darkMode;
			}
			if (this.map) {
				this.applyMapStyle();
			}
		});
	};

	setupDarkModeListener(): void {
		if (this.darkModeMediaQuery.addEventListener) {
			this.darkModeMediaQuery.addEventListener('change', this.darkModeHandler);
		} else if ((this.darkModeMediaQuery as unknown as { addListener?: (handler: (e: MediaQueryListEvent) => void) => void }).addListener) {
			(this.darkModeMediaQuery as unknown as { addListener: (handler: (e: MediaQueryListEvent) => void) => void }).addListener(this.darkModeHandler);
		}
	}

	setDarkMode(isDark: boolean): void {
		this.darkMode = isDark;
		if (this.view) {
			this.view.darkMode = isDark;
		}
		if (this.map) {
			this.applyMapStyle();
		}
	}

	override onDestroy(): void {
		super.onDestroy();
		if (this.darkModeMediaQuery.removeEventListener) {
			this.darkModeMediaQuery.removeEventListener('change', this.darkModeHandler);
		} else if ((this.darkModeMediaQuery as unknown as { removeListener?: (handler: (e: MediaQueryListEvent) => void) => void }).removeListener) {
			(this.darkModeMediaQuery as unknown as { removeListener: (handler: (e: MediaQueryListEvent) => void) => void }).removeListener(this.darkModeHandler);
		}
		this.clearMapOverlays();
		this.map = null as unknown as google.maps.Map;
		this.isMapInitialized = false;
	}

	applyMapStyle(): void {
		if (!this.map) return;
		const style = this.darkMode ? DARK_MAP_STYLES : LIGHT_MAP_STYLES;
		this.map.setOptions({ styles: style as google.maps.MapTypeStyle[] });

		const circleOptions = this.darkMode ? DARK_CIRCLE_OPTIONS : LIGHT_CIRCLE_OPTIONS;
		this.circles.forEach(circle => {
			circle.setOptions(circleOptions);
		});
	}

	private getContainerElement(): HTMLElement | null {
		return this.view?.mapContainer?.nativeElement ?? null;
	}

	private getStoreCoordinates(store: StoreModel): google.maps.LatLngLiteral | null {
		if (!store) return null;

		if (store.location && typeof store.location.lat === 'number' && typeof store.location.lng === 'number') {
			if (!isNaN(store.location.lat) && !isNaN(store.location.lng) && (store.location.lat !== 0 || store.location.lng !== 0)) {
				return { lat: store.location.lat, lng: store.location.lng };
			}
		}

		const rawLoc = store.location as unknown as { lat?: unknown; lng?: unknown } | null;
		if (rawLoc && rawLoc.lat != null && rawLoc.lng != null) {
			const lat = parseFloat(String(rawLoc.lat));
			const lng = parseFloat(String(rawLoc.lng));
			if (!isNaN(lat) && !isNaN(lng) && (lat !== 0 || lng !== 0)) {
				return { lat, lng };
			}
		}

		if (Array.isArray(store.delivery_zone)) {
			for (const zone of store.delivery_zone) {
				if (Array.isArray(zone.puntoMapa) && zone.puntoMapa.length > 0) {
					const first = zone.puntoMapa[0];
					const lat = parseFloat(String(first.lat));
					const lng = parseFloat(String(first.lng));
					if (!isNaN(lat) && !isNaN(lng) && (lat !== 0 || lng !== 0)) {
						return { lat, lng };
					}
				}
			}
		}

		return null;
	}

	private calculateCenter(storeList: StoreModel[], clientLocation: ClientLocation | null): google.maps.LatLngLiteral {
		if (clientLocation && typeof clientLocation.lat === 'number' && typeof clientLocation.lng === 'number' && (clientLocation.lat !== 0 || clientLocation.lng !== 0)) {
			return { lat: clientLocation.lat, lng: clientLocation.lng };
		}

		for (const store of storeList) {
			const coords = this.getStoreCoordinates(store);
			if (coords) {
				return coords;
			}
		}

		return { lat: 25.7617, lng: -80.1918 };
	}

	initMap(storeList: StoreModel[], clientLocation: ClientLocation | null): void {
		this.cachedStores = storeList || [];
		this.cachedClientLocation = clientLocation;
		this.tryInitOrUpdateMap();
	}

	tryInitOrUpdateMap(): void {
		const container = this.getContainerElement();
		if (!container) return;

		if (typeof google === 'undefined' || !google?.maps) {
			setTimeout(() => {
				if (typeof google !== 'undefined' && google?.maps) {
					this.tryInitOrUpdateMap();
				}
			}, 300);
			return;
		}

		const defaultCenter = this.calculateCenter(this.cachedStores, this.cachedClientLocation);

		if (!this.map || !this.isMapInitialized) {
			const mapOptions: google.maps.MapOptions = {
				center: defaultCenter,
				zoom: 15,
				mapTypeId: google.maps.MapTypeId.ROADMAP,
				fullscreenControl: false,
				streetViewControl: false,
				mapTypeControl: false,
				styles: (this.darkMode ? DARK_MAP_STYLES : LIGHT_MAP_STYLES) as google.maps.MapTypeStyle[],
			};

			this.map = new google.maps.Map(container, mapOptions);
			this.isMapInitialized = true;
			this.setupMapClickListener();
		}

		this.clearMapOverlays();

		if (this.cachedStores && this.cachedStores.length > 0) {
			this.drawStoreMarkers(this.cachedStores);
			this.drawDeliveryZones(this.cachedStores);
		}

		this.updateUserMarker(this.cachedClientLocation);

		if (this.markers.length > 0 || this.userMarker) {
			this.fitMapBounds();
		} else {
			this.map.panTo(defaultCenter);
		}

		setTimeout(() => {
			if (this.map && typeof google !== 'undefined' && google?.maps) {
				google.maps.event.trigger(this.map, 'resize');
				if (this.markers.length > 0 || this.userMarker) {
					this.fitMapBounds();
				}
			}
		}, 300);
	}

	private clearMapOverlays(): void {
		this.markers.forEach(marker => marker.setMap(null));
		this.markers = [];
		this.circles.forEach(circle => circle.setMap(null));
		this.circles = [];
		if (this.userMarker) {
			this.userMarker.setMap(null);
			this.userMarker = null;
		}
	}

	private createMarkerBadgeIcon(options: {
		svgContent: string;
		backgroundColor: string;
		iconColor?: string;
		size?: number;
		iconSize?: number;
	}): google.maps.Icon {
		const {
			svgContent,
			backgroundColor,
			iconColor = '#ffffff',
			size = 40,
			iconSize = 22,
		} = options;

		const innerPaths = svgContent
			.replace(/<svg[^>]*>/, '')
			.replace(/<\/svg>/, '')
			.replace(/stroke="[^"]*"/gi, `stroke="${iconColor}"`)
			.replace(/fill="(?!none)[^"]*"/gi, `fill="${iconColor}"`)
			.replace(/currentColor/gi, iconColor);

		const radius = (size - 4) / 2;
		const center = size / 2;
		const offset = (size - iconSize) / 2;
		const scale = (iconSize / 24).toFixed(4);

		const svg = `<svg width="${size}" height="${size}" viewBox="0 0 ${size} ${size}" fill="none" xmlns="http://www.w3.org/2000/svg">
  <circle cx="${center}" cy="${center + 1.5}" r="${radius}" fill="rgba(0,0,0,0.22)"/>
  <circle cx="${center}" cy="${center}" r="${radius}" fill="${backgroundColor}"/>
  <g transform="translate(${offset}, ${offset}) scale(${scale})">
    ${innerPaths.trim()}
  </g>
</svg>`;

		const encodedSvg = 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(svg);

		return {
			url: encodedSvg,
			scaledSize: new google.maps.Size(size, size),
			anchor: new google.maps.Point(center, center),
		};
	}

	private drawStoreMarkers(storeList: StoreModel[]): void {
		this.markers.forEach(marker => marker.setMap(null));
		this.markers = [];

		if (!storeList || storeList.length === 0 || !this.map) return;

		const applyStoreMarkers = (icon: google.maps.Icon): void => {
			storeList.forEach(store => {
				const coords = this.getStoreCoordinates(store);
				if (coords && this.map) {
					const marker = new google.maps.Marker({
						position: coords,
						map: this.map,
						title: store.nombre || 'Tienda',
						icon,
					});
					this.markers.push(marker);
				}
			});
			this.fitMapBounds();
		};

		const storeColor = this.getStoreColor();
		const cachedIcon = this.storeBadgeIcon;
		if (cachedIcon) {
			applyStoreMarkers(cachedIcon);
		} else {
			this.iconService.getIcon('building-store').subscribe(svgStr => {
				const icon = this.createMarkerBadgeIcon({
					svgContent: svgStr,
					backgroundColor: storeColor,
					iconColor: '#ffffff',
					size: 40,
					iconSize: 22,
				});
				this.storeBadgeIcon = icon;
				applyStoreMarkers(icon);
			});
		}
	}

	private drawDeliveryZones(storeList: StoreModel[]): void {
		this.circles.forEach(circle => circle.setMap(null));
		this.circles = [];

		if (!storeList || storeList.length === 0 || !this.map) return;

		storeList.forEach(store => {
			if (Array.isArray(store.delivery_zone)) {
				store.delivery_zone.forEach(
					(zone: { tipoZonaMapa: string; puntoMapa: string | DeliveryZoneMapa[]; color?: string }) => {
						if (
							zone.tipoZonaMapa === 'CIRCULO' &&
							Array.isArray(zone.puntoMapa) &&
							zone.puntoMapa.length > 0
						) {
							const first = zone.puntoMapa[0];
							const lat = typeof first.lat === 'number' ? first.lat : parseFloat(String(first.lat));
							const lng = typeof first.lng === 'number' ? first.lng : parseFloat(String(first.lng));
							const radio = typeof first.radio === 'number' ? first.radio : parseFloat(String(first.radio));

							if (!isNaN(lat) && !isNaN(lng) && !isNaN(radio) && radio > 0) {
								const baseCircleOptions = this.darkMode ? DARK_CIRCLE_OPTIONS : LIGHT_CIRCLE_OPTIONS;
								const circleOptions = {
									...baseCircleOptions,
									...(zone.color ? { fillColor: zone.color, strokeColor: zone.color } : {})
								};
								const _circle = new google.maps.Circle({
									...circleOptions,
									clickable: false,
									map: this.map,
									center: { lat, lng },
									radius: radio * 1000,
								});
								this.circles.push(_circle);
							}
						}
					}
				);
			}
		});
	}

	private fitMapBounds(): void {
		if (!this.map) return;
		if (this.markers.length === 0 && !this.userMarker) return;

		if (this.markers.length === 1 && !this.userMarker) {
			const pos = this.markers[0].getPosition();
			if (pos) {
				this.map.panTo(pos);
				this.map.setZoom(15);
			}
			return;
		}

		const bounds = new google.maps.LatLngBounds();
		this.markers.forEach(marker => {
			const pos = marker.getPosition();
			if (pos) bounds.extend(pos);
		});
		if (this.userMarker) {
			const pos = this.userMarker.getPosition();
			if (pos) bounds.extend(pos);
		}
		this.map.fitBounds(bounds);
	}

	private setupMapClickListener(): void {
		if (!this.map) return;
		this.map.addListener('click', (e: google.maps.MapMouseEvent) => {
			if (e.latLng) {
				const geocoder = new google.maps.Geocoder();
				geocoder.geocode({ location: e.latLng }, (results, status) => {
					if (status === 'OK' && results && results[0]) {
						this.view.addressSelected.emit(results[0].formatted_address);
					}
				});
			}
		});
	}

	updateUserMarker(clientLocation: ClientLocation | null): void {
		if (!this.map) return;
		if (this.userMarker) {
			this.userMarker.setMap(null);
			this.userMarker = null;
		}
		if (clientLocation && clientLocation.lat && clientLocation.lng) {
			const pos = { lat: Number(clientLocation.lat), lng: Number(clientLocation.lng) };
			this.map.panTo(pos);
			this.map.setZoom(15);

			const createOrSetUserMarker = (icon: google.maps.Icon): void => {
				if (this.userMarker) {
					this.userMarker.setMap(null);
				}
				this.userMarker = new google.maps.Marker({
					position: pos,
					map: this.map,
					title: 'Current Location',
					clickable: false,
					icon,
				});
			};

			const userColor = this.getUserLocationColor();
			const cachedUserIcon = this.userBadgeIcon;
			if (cachedUserIcon) {
				createOrSetUserMarker(cachedUserIcon);
			} else {
				this.iconService.getIcon('map-pin').subscribe(svgStr => {
					const icon = this.createMarkerBadgeIcon({
						svgContent: svgStr,
						backgroundColor: userColor,
						iconColor: '#ffffff',
						size: 40,
						iconSize: 22,
					});
					this.userBadgeIcon = icon;
					createOrSetUserMarker(icon);
				});
			}
		}
	}
}
